import { db, appId } from '../firebase';
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    arrayUnion,
    arrayRemove,
} from 'firebase/firestore';
import { Quiz, QuizSummary, MyQuizSelection } from '../types';
import { logger } from '../utils/logger';


//キャッシュの設定
const QUIZ_CACHE_KEY_PREFIX = 'quiz-cache-';
const CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 10; // 10日
const getCacheKey = (quizId: string) => `${QUIZ_CACHE_KEY_PREFIX}${quizId}`;


//Firebaseのパス
export const getMyQuizSelectionRef = (userId: string) =>
    doc(db, `artifacts/${appId}/users/${userId}/userData`, 'MyQuizSelection');


/**
 * クイズを元に、クイズサマリーを作成します。
 * @param  quiz
 * @param  isGiftedItem ギフトコードでクイズを追加した場合true
 * @return クイズサマリー
*/
function createQuizSummary(
    quiz: Quiz,
    isGiftedItem: boolean = false // デフォルト値として false を設定
): QuizSummary {
    const { id, title, category, isPublic, authorId, authorNickname, questions } = quiz;
    return { id, title, category, isPublic, authorId, authorNickname, questionCount: questions.length , isGiftedItem};
}




/**
 * クイズを元にMyQuizSelectionに追加または更新します。
 * 新しいクイズの場合は追加し、既存のクイズの場合は概要を更新します。
 * hiddenQuizIdListは本関数では変更しません。
 *
 * @param userId ユーザーID
 * @param quiz 追加または更新するクイズオブジェクト（createdAt, updatedAtを除く）
 * @param isGiftedItem 追加または更新するクイズがギフトコードで登録されたかのフラグ
 */
export async function addMyQuizSelection(
    userId: string,
    quiz: Omit<Quiz, 'createdAt' | 'updatedAt'>,
    isGiftedItem: boolean = false // デフォルト値として false を設定
    ){

        const ref = getMyQuizSelectionRef(userId);
        const snap = await getDoc(ref);

        let currentQuizSummaries: QuizSummary[] = [];
        let currentHiddenQuizIdList: string[] = []; // hiddenQuizIdListも取得しておく

        if (snap.exists()) {
            const data = snap.data() as MyQuizSelection;
            currentQuizSummaries = data.quizSummaries || [];
            currentHiddenQuizIdList = data.hiddenQuizIdList || []; // hiddenQuizIdListの取得
        }

        const newSummary = createQuizSummary(quiz, isGiftedItem); // 最新のクイズ概要を作成


        logger.info("newSummary:", newSummary);

        let updatedQuizSummaries: QuizSummary[];

        // 既に存在するクイズかどうかをIDでチェック
        const existingIndex = currentQuizSummaries.findIndex(q => q.id === quiz.id);

        if (existingIndex !== -1) {
            // 既存のクイズがある場合：その概要を更新
            updatedQuizSummaries = [...currentQuizSummaries]; // スプレッド構文で新しい配列を作成
            updatedQuizSummaries[existingIndex] = newSummary; // 既存の要素を更新
            logger.info(`Quiz ${quiz.id} summary updated in MyQuizSelection for user ${userId}.`);
        } else {
            // 新規のクイズの場合：配列に追加
            updatedQuizSummaries = [...currentQuizSummaries, newSummary];
            logger.info(`Quiz ${quiz.id} added to MyQuizSelection for user ${userId}.`);
        }

        // ドキュメントを更新または作成
        // quizSummaries フィールドのみを更新し、hiddenQuizIdList は既存のものを保持します。
        // setDoc({ merge: true }) を使うことで、hiddenQuizIdList があれば上書きせず残します。
        await setDoc(ref, {
            quizSummaries: updatedQuizSummaries,
            // hiddenQuizIdList は既存のものをそのまま保持
            // snap.exists() で取得した currentHiddenQuizIdList を利用
            hiddenQuizIdList: currentHiddenQuizIdList // 明示的に既存のリストを渡す
        }, { merge: true });
}

/**
 * 指定されたクイズをMyQuizSelectionから削除します
 * Summaryとhiddenから削除されます。
 * ※クイズ本体の削除は行いません。
*/
export async function removeQuizfromMyQuizSelection(userId: string, quizId: string) {
    const ref = getMyQuizSelectionRef(userId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;

    const current = snap.data() as MyQuizSelection;
    const summaries = current.quizSummaries || [];
    const hidden = current.hiddenQuizIdList || [];

    //サマリーとHiddenから対象クイズを削除
    const newSummaries = summaries.filter((q) => q.id !== quizId);
    const newHidden = hidden.filter((id) => id !== quizId);

    await updateDoc(ref, {
        quizSummaries: newSummaries,
        hiddenQuizIdList: newHidden,
    });
}


export async function fetchMyQuizSelection(userId: string): Promise<MyQuizSelection | null> {


    logger.info("fetch MyQuizSelection: userId=", userId);

    const ref = getMyQuizSelectionRef(userId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return snap.data() as MyQuizSelection;
}

export async function hideQuiz(userId: string, quizId: string): Promise<void> {

    const ref = getMyQuizSelectionRef(userId);
    await updateDoc(ref, {
        hiddenQuizIdList: arrayUnion(quizId),
    });
}

export async function showQuiz(userId: string, quizId: string): Promise<void> {
    const ref = getMyQuizSelectionRef(userId);
    await updateDoc(ref, {
        hiddenQuizIdList: arrayRemove(quizId),
    });
}
